import type { AxiosError } from "axios";
import Axios from "axios";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { fontSizeTokens, spacingTokens } from "../../designTokens";
import { H3 } from "../Typography/Typography";
import {
  ButtonContainer,
  DestructiveButtonLargeV2,
  GhostButtonMediumV2,
  LinkButtonMediumV2,
  PrimaryButtonLargeV2,
} from "../Buttons/Buttons";
import {
  formatDate,
  formatDateTime,
  toTitleCase,
  useStoreState,
} from "../../util/util";
import { Notifications } from "../Notifications/NotificationsContext";
import { CaretLeft, EditIcon } from "../Icons/Icons";
import { Loader } from "../Loader/Loader";
import { HeaderLeft, HeaderRight, PageHeader } from "../Layout/Layout";
import { ContentWrapper } from "../../layout/publicPageLayout";
import { IDDesktop } from "../IDs/IDs";
import { TableContainer } from "../../pages/SharedPages/ProductDetailPage/Assets/util";
import {
  HandleLeadNoteSubmitProps,
  LeadNotes,
} from "../../pages/SharedPages/LeadsPage/LeadNotes";
import { EditAssigneeSlideOut } from "./EditAssignee";
import { SectionTitle } from "../Form/Form";
import { ErrorPlaceholder } from "../Error";
import { endpoints } from "../../endpoints";
import { LeadNote } from "../../types/types";
import axios from "axios";
import {
  buildCollectionTableData,
  ChangeRequestContextType,
  ChangeRequestStatus,
  ChangeRequestStatusType,
} from "./changerequest.util";
import { InfoBlockSmall } from "../InfoBlocks/InfoBlocks";
import { Table } from "../Table/Table";
import {
  Assets,
  AttributeDataType,
  AttributeInput,
  AttributeSchema,
} from "../../types/types.PIM";
import {
  CollectionAttributeDisplayValue,
  getCollectionAttributeDisplayValue,
} from "../../util/collectionAttributeDisplayValue";
import { IRow } from "../../pages/admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { ScrollTableWrapper } from "../../pages/SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { useAuthContext } from "../Auth";
import { useRoutePath } from "../../util/Routing";

export interface ChangeRequestUser {
  id: string;
  firstname: string;
  lastname: string;
  username?: string;
  email_address: string;
}

interface ChangeRequestNote {
  id: string;
  text: string;
  created_by: ChangeRequestUser;
  created_at: string;
}

interface ContextSnapshotRowValue {
  attribute_id: string;
  data_type: AttributeDataType;
  id: string;
  modified_at: string;
  priority: number;
  value: string;
  enum_variant?: {
    attribute_enum_metadata_id: string;
    has_cdn_image: boolean;
    id: string;
    variant: string;
    variant_type: string;
  };
}

interface ContextSnapshotRow {
  id: string;
  values: {
    collection_id: string;
    row_id: string;
    values: ContextSnapshotRowValue[];
  };
}

interface ContextSnapshot {
  attributes: {
    input_type: AttributeInput;
    name: string;
    id: string;
  }[];
  collection?: {
    id: string;
    name: string;
  };
  asset?: Assets;
  context_label: string;
  context_type: ChangeRequestContextType;
  row?: ContextSnapshotRow;
}

interface ChangeRequest {
  id: string;
  number: number;
  context_type: ChangeRequestContextType;
  context_label: string;
  asset_fields: string[];
  details: string;
  status: ChangeRequestStatusType;
  requested_by: ChangeRequestUser;
  assignee: ChangeRequestUser;
  context_snapshot: ContextSnapshot;
  created_at: string;
  notes: ChangeRequestNote[];
  attributes: { id: string; name: string }[];
}

const MetaData = styled.span`
  font-size: 12px;
  color: #888;
`;

const RequestCardLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-self: start;
  gap: 6px;
`;

const RequestCardRight = styled.div`
  justify-self: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: start;
`;

const RequestInfoCard = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start;

  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  width: 100%;
`;
const EditActions = styled.div`
  margin-left: auto;
  justify-self: end;
`;

const assetFields = [
  "name",
  "asset_category",
  "language",
  "country_region",
  "issue_date",
  "expiration_date",
  "document_type",
];

interface ChangeRequestDetailPageProps {
  changeRequestId: string;
  onBack: () => void;
  canEdit: boolean;
}

export const ChangeRequestDetailPage = ({
  changeRequestId,
  onBack,
  canEdit,
}: ChangeRequestDetailPageProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { tenant_id } = useStoreState();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const [isLoading, setIsLoading] = useState(false);
  const [enableEditAsignee, setEnableEditAssignee] = useState(false);
  const { roleIsSellerAdmin, hasPermission, user } = useAuthContext();
  const { storePath, adminPath } = useRoutePath();
  const {
    data: changeRequest,
    error,
    mutate,
  } = useSWR<ChangeRequest, AxiosError>(
    endpoints.v2_tenants_id_change_requests_id(tenant_id, changeRequestId)
  );

  const handleUpdateStatus = async (status: string) => {
    setIsLoading(true);
    try {
      await axios.patch(
        endpoints.v2_tenants_id_change_requests_id(tenant_id, changeRequestId),
        {
          status: status,
        }
      );
      mutate();
      notifySuccess(t("Change request status updated!"));
    } catch (error) {
      notifyError(t("There was an error updating the status"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async ({
    note,
    onSuccess,
  }: HandleLeadNoteSubmitProps) => {
    if (!note.trim() || !canEdit) return;
    try {
      await Axios.post(
        endpoints.v2_tenants_id_change_requests_id_notes(
          tenant_id,
          changeRequestId
        ),
        { text: note }
      );
      notifySuccess(t("Note added successfully"));
      onSuccess();
      mutate();
    } catch (err) {
      notifyError(t("There was an error adding the note"), { error: err });
    }
  };

  const fields = changeRequest?.attributes?.length
    ? changeRequest?.attributes
    : changeRequest?.asset_fields ?? [];

  const collectionTableColumns = useMemo(() => {
    if (!changeRequest || changeRequest?.context_type !== "collection") {
      return [];
    }

    const snapshotAttributes = changeRequest.context_snapshot?.attributes;
    if (!snapshotAttributes) return [];

    return snapshotAttributes.map((attr) => ({
      header: attr.name,
      accessorKey: attr.id,
      cell: ({
        getValue,
      }: {
        row: { original: IRow };
        getValue: () => CollectionAttributeDisplayValue;
      }) =>
        getCollectionAttributeDisplayValue(
          { ...attr, input_type: attr.input_type } as AttributeSchema,
          getValue() as CollectionAttributeDisplayValue,
          {
            theme,
            t,
            user,
            storePath,
            roleIsSellerAdmin,
            hasPermission,
            adminPath,
          }
        ),
    }));
  }, [
    adminPath,
    changeRequest,
    hasPermission,
    roleIsSellerAdmin,
    storePath,
    t,
    theme,
    user,
  ]);

  const collectionTableData = useMemo(() => {
    if (!changeRequest || changeRequest?.context_type !== "collection") {
      return [];
    }
    const snapshotRow = changeRequest.context_snapshot.row;
    if (!snapshotRow) return [];

    const row = {
      row_id: snapshotRow.values.row_id,
      values: snapshotRow.values.values,
    };
    const cells = buildCollectionTableData(row);
    return [
      cells.reduce<Record<string, any>>((acc, { column, value }) => {
        acc[column.id] = value;
        return acc;
      }, {}),
    ];
  }, [changeRequest]);

  const assetTableColumns = useMemo(() => {
    if (!changeRequest || changeRequest.context_type !== "asset") return [];
    return assetFields.map((field) => ({
      header: field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      accessorKey: field,
      cell: ({ getValue }: { getValue: () => any }) =>
        field === "host" ? getValue()?.name ?? "Agilis" : getValue() ?? "--",
    }));
  }, [changeRequest]);

  const assetTableData = useMemo(() => {
    if (!changeRequest || changeRequest.context_type !== "asset") return [];
    const asset = changeRequest.context_snapshot.asset;
    if (!asset) return [];
    return [
      {
        name: asset.name ?? "--",
        asset_category: asset.asset_category ?? "--",
        host: {
          id: asset.host?.id ?? "",
          name: asset.host?.name ?? "Agilis",
        },
        language: asset.language?.toUpperCase() ?? "--",
        country_region: `${asset.country ?? "--"} / ${asset.region ?? "--"}`,
        issue_date: asset.issue_date
          ? formatDate(asset.issue_date)
          : "--",
        expiration_date: asset.expiration_date
          ? formatDate(asset.expiration_date)
          : "--",
        document_type: asset.document_type ?? "--",
      },
    ];
  }, [changeRequest]);

  if (error) {
    return (
      <>
        <ErrorPlaceholder
          message={t("There was an error loading the change request")}
        />
      </>
    );
  }

  if (!changeRequest) {
    return (
      <>
        <Loader isLoading={true} />
      </>
    );
  }

  return (
    <>
      <LinkButtonMediumV2 onClick={onBack}>
        <CaretLeft />
        {t("Change Requests")}
      </LinkButtonMediumV2>

      <PageHeader style={{ marginTop: spacingTokens.spacing_md }}>
        <HeaderLeft>
          <div
            style={{
              display: "flex",
              gap: spacingTokens.spacing_md,
              marginTop: spacingTokens.spacing_3xl,
              alignItems: "center",
            }}
          >
            <SectionTitle style={{ margin: "0" }}>
              {t("Change Request")}
            </SectionTitle>
            {changeRequest.number && (
              <IDDesktop
                style={{
                  fontSize: fontSizeTokens.font_size_md,
                }}
              >
                {changeRequest.number}
              </IDDesktop>
            )}
          </div>
        </HeaderLeft>
        <HeaderRight>
          <ChangeRequestStatus status={changeRequest.status} />
        </HeaderRight>
      </PageHeader>

      <ContentWrapper style={{ marginTop: spacingTokens.spacing_xl }}>
        <RequestInfoCard>
          <RequestCardLeft>
            <div>
              <InfoBlockSmall
                header="Requested By"
                content={
                  changeRequest.requested_by.firstname +
                  " " +
                  changeRequest.requested_by.lastname
                }
              ></InfoBlockSmall>
              <MetaData>{formatDateTime(changeRequest.created_at)}</MetaData>
            </div>
            <div style={{ marginTop: spacingTokens.spacing_xl }}>
              <InfoBlockSmall
                header={toTitleCase(changeRequest.context_type) + "  Name"}
                content={changeRequest.context_label}
              ></InfoBlockSmall>
            </div>
            <div style={{ marginTop: spacingTokens.spacing_xl }}>
              <InfoBlockSmall
                header="Request Details"
                content={changeRequest.details}
              ></InfoBlockSmall>
            </div>
          </RequestCardLeft>

          <RequestCardRight>
            <div>
              <InfoBlockSmall
                header="Assignee"
                content={`${changeRequest.assignee?.firstname ?? "-"} ${
                  changeRequest.assignee?.lastname ?? ""
                }`.trim()}
              ></InfoBlockSmall>
              {changeRequest.assignee && (
                <MetaData>{changeRequest.assignee.email_address}</MetaData>
              )}
            </div>

            {fields.length > 0 && (
              <div style={{ marginTop: spacingTokens.spacing_xl }}>
                <InfoBlockSmall
                  header={
                    changeRequest.context_type === "asset"
                      ? "Fields"
                      : "Attributes"
                  }
                  content={fields
                    .map((el) =>
                      (typeof el === "string"
                        ? toTitleCase(el)
                        : toTitleCase(el.name)
                      ).replace(/_/g, " ")
                    )
                    .join(", ")}
                />
              </div>
            )}
          </RequestCardRight>

          {canEdit && (
            <EditActions>
              <GhostButtonMediumV2
                onClick={() => setEnableEditAssignee(true)}
                title={t("Edit")}
                style={{ background: "transparent" }}
              >
                <EditIcon />
              </GhostButtonMediumV2>
            </EditActions>
          )}
        </RequestInfoCard>

        {changeRequest.context_type === "collection" &&
          collectionTableData.length > 0 && (
            <>
              <H3>{t("Row Details")}</H3>
              <TableContainer>
                <ScrollTableWrapper>
                  <Table
                    columns={collectionTableColumns}
                    data={collectionTableData}
                    isLoading={false}
                    enableDefaultSort={true}
                    error={undefined}
                    lastChildleftAlign
                    highlightLinks
                  />
                </ScrollTableWrapper>
              </TableContainer>
            </>
          )}

        {changeRequest.context_type === "asset" && assetTableData.length > 0 && (
          <>
            <H3>{t("Asset Details")}</H3>
            <TableContainer>
              <ScrollTableWrapper>
                <Table
                  columns={assetTableColumns}
                  data={assetTableData}
                  isLoading={false}
                  enableDefaultSort={false}
                  error={undefined}
                  lastChildleftAlign
                  highlightLinks
                />
              </ScrollTableWrapper>
            </TableContainer>
          </>
        )}

        {/* Action buttons */}
        {canEdit && (
          <ButtonContainer style={{ marginTop: spacingTokens.spacing_lg }}>
            <DestructiveButtonLargeV2
              disabled={Boolean(
                changeRequest.status === "discarded" || isLoading
              )}
              onClick={() => handleUpdateStatus("discarded")}
            >
              {t("Discard")}
            </DestructiveButtonLargeV2>
            <PrimaryButtonLargeV2
              disabled={
                Boolean(changeRequest.status === "accepted") || isLoading
              }
              onClick={() => handleUpdateStatus("accepted")}
            >
              {t("Accept")}
            </PrimaryButtonLargeV2>
          </ButtonContainer>
        )}

        {/* Notes section */}
        <LeadNotes
          internal_notes={
            (changeRequest.notes ?? []).map((note) => ({
              id: note.id,
              message: note.text,
              creator_name: `${note.created_by.firstname} ${note.created_by.lastname}`,
              created_at: note.created_at,
              note_type: "standard",
              is_system_generated: false,
            })) as unknown as LeadNote[]
          }
          cardStyle={{ background: "transparent", paddingTop: 0 }}
          handleSubmit={handleAddNote}
          readyOnly={!canEdit}
        />
      </ContentWrapper>

      {enableEditAsignee && (
        <EditAssigneeSlideOut
          assignee={changeRequest.assignee}
          changeRequestId={changeRequestId}
          onClose={() => {
            setEnableEditAssignee(false);
            mutate();
          }}
        />
      )}
    </>
  );
};
